<template>
  <div id="award-category-create">
    <overlay-loading-progress
      :LoadingFlag="OverlayLoadingFlag"
    ></overlay-loading-progress>
    <v-card elevation="8" class="rounded-xl">
      <!-- Header Section with Gradient -->
      <v-card-title class="page-header-gradient white--text d-flex align-center pa-5">
        <v-icon left large color="white" class="mr-3">mdi-trophy-award</v-icon>
        <h2 class="text-h5 font-weight-bold">{{ PageTitle }}</h2>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="closePrompt" large>
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-card-text class="pa-6">
        <!-- Loading Indicator -->
        <div class="d-flex justify-center mb-3" v-if="LoadingFlag">
          <b-spinner
            variant="primary"
            type="grow"
            label="Loading..."
          ></b-spinner>
        </div>

        <v-form
          ref="form1"
          v-model="valid1"
          lazy-validation
          v-on:submit.prevent="confirmAlert"
        >
          <v-container class="py-0">
            <!-- Required Field Note -->
            <v-row>
              <v-col cols="12">
                <v-alert
                  type="info"
                  dense
                  text
                  outlined
                  class="mb-4"
                >
                  <small><span class="text-danger">*</span> indicates required field</small>
                </v-alert>
              </v-col>
            </v-row>

            <v-row>
              <!-- Category Title -->
              <v-col cols="12" sm="6">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Category Title
                </h6>
                <v-text-field
                  v-model="CategoryTitle"
                  placeholder="Enter Category Title"
                  :rules="CategoryTitleRules"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-format-title"
                ></v-text-field>
              </v-col>

              <!-- Attachment Maximum Count -->
              <v-col cols="12" sm="6">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Attachment Maximum Count
                </h6>
                <v-text-field
                  v-model="CategoryMaxCount"
                  type="number"
                  placeholder="Enter Maximum Count"
                  :rules="CategoryMaxCountRules"
                  required
                  outlined
                  dense
                  prepend-inner-icon="mdi-counter"
                ></v-text-field>
              </v-col>

              <!-- Category Description -->
              <v-col cols="12">
                <h6 class="mb-2 font-weight-bold">Category Description</h6>
                <v-textarea
                  v-model="CategoryDescription"
                  placeholder="Enter Category Description"
                  :rules="CategoryDescriptionRules"
                  outlined
                  dense
                  rows="3"
                  prepend-inner-icon="mdi-text"
                ></v-textarea>
              </v-col>

              <!-- Portfolio Type -->
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Portfolio Type
                </h6>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="PortfolioTypeOptions"
                  :rules="PortfolioTypeRules"
                  v-model="PortfolioType"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-briefcase"
                ></v-autocomplete>
              </v-col>

              <!-- Category Type -->
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Category Type
                </h6>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :items="CategoryTypeOptions"
                  :rules="CategoryTypeRules"
                  v-model="CategoryType"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-shape"
                ></v-autocomplete>
              </v-col>

              <!-- Recognition Gift -->
              <v-col cols="12" sm="6" md="4">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Recognition Gift
                </h6>
                <v-autocomplete
                  :items="RecognitionGiftOptions"
                  v-model="RecognitionGift"
                  :rules="RecognitionGiftRules"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-gift"
                ></v-autocomplete>
              </v-col>

              <!-- Governing Board Type (Conditional) -->
              <v-col cols="12" sm="6" md="4" v-if="CategoryType == 3 && OrganizationTypeFlag">
                <h6 class="mb-2 font-weight-bold">
                  <span class="text-danger">*</span> Governing Board Type
                </h6>
                <v-select
                  @change="getDesignationCodeOptions"
                  :reduce="(option) => option.value"
                  :items="OrganizationTypeOptions"
                  :rules="OrganizationTypeRules"
                  v-model="OrganizationType"
                  dense
                  clearable
                  required
                  outlined
                  prepend-inner-icon="mdi-office-building"
                ></v-select>
              </v-col>

              <!-- Designation (Conditional - Organization Type 3) -->
              <v-col
                cols="12"
                sm="6"
                md="4"
                v-if="CategoryType == 3"
              >
                <h6 class="mb-2 font-weight-bold">Designation</h6>
                <v-autocomplete
                  :reduce="(option) => option.value"
                  :loading="DesignationCodeOptionsLoading"
                  :items="DesignationCodeOptions"
                  :rules="DesignationCodeRules"
                  v-model="DesignationCode"
                  dense
                  clearable
                  outlined
                  multiple
                  chips
                  deletable-chips
                  prepend-inner-icon="mdi-account-tie"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-container>

          <!-- Action Buttons -->
          <v-divider class="my-4"></v-divider>
          <v-row>
            <v-col cols="12" class="text-center">
              <v-btn
                :disabled="!valid1"
                @click.prevent="confirmAlert"
                :loading="SubmitFlag"
                elevation="12"
                rounded
                large
                color="#1db954"
                class="font-size-h6 px-8 mr-3 white--text"
              >
                <v-icon left>mdi-check-circle</v-icon>
                Submit
              </v-btn>
              <v-btn
                @click="closePrompt"
                elevation="12"
                rounded
                large
                color="#757575"
                class="font-size-h6 px-8 white--text"
              >
                <v-icon left>mdi-close-circle</v-icon>
                Close
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import common from "@/view/Common.vue";
import companyConfig from "@/company_config.json";
import Swal from "sweetalert2";

export default {
  mixins: [common],
  components: {},
  props: {
    addRecordPrompt: {
      required: true,
      type: Boolean,
    },
  },
  data() {
    return {
      valid1: true,
      valid2: true,
      LoadingFlag: false,
      SubmitFlag: false,
      OverlayLoadingFlag: false,

      UploadFlag: false,
      ResultFlag: false,

      CategoryTitle: "",
      CategoryTitleRules: [(v) => !!v || "Category Title is required"],

      CategoryDescription: "",
      CategoryDescriptionRules: [],

      CategoryMaxCount: 0,
      CategoryMaxCountRules: [
        (v) => !!v || "Category Maximum Count is required",
      ],

      CategoryTypeRules: [(v) => !!v || "Category Type is required"],
      CategoryType: "",
      CategoryTypeOptions: [
        {
          value: "",
          text: "-- Select --",
        },
        {
          value: 1,
          text: "LO",
        },
        {
          value: 2,
          text: "Member",
        },
        {
          value: 3,
          text: "Designation",
        },
      ],

      PortfolioTypeRules: [(v) => !!v || "Portfolio Type is required"],
      PortfolioType: "",
      PortfolioTypeOptions: [],

      OrganizationTypeFlag: false,
      OrganizationType: 2,
      OrganizationTypeRules: [(v) => !!v || "Governing Board Type is required"],
      OrganizationTypeOptions: [
        { value: "", text: "--Select--" },
        { value: 1, text: "National" },
        { value: 2, text: "Zone" },
        { value: 3, text: "LO" },
      ],
      OrganizationTypeOptionsLoading: false,

      RecognitionGiftOptions: [],
      RecognitionGiftRules: [(v) => !!v || " Recognition Gift is required"],
      RecognitionGiftOptionsLoading: false,
      RecognitionGift: "",

      DesignationCode: [],
      DesignationCodeRules: [],
      DesignationCodeOptions: [],
      DesignationCodeOptionsLoading: false,

      PageInfo: {},
      PageTitle: "",
      PageDescription: "",
      CurrentYearId: 0,
      YearName: "",
      NextYearCode: "",
      NextYearName: "",
    };
  },
  computed: {},
  watch: {
    PageInfo: function () {
      console.log("watch PageInfo");
      this.LoadingFlag = false;
      var tr = this.PageInfo;
      console.log({ tr });
      var n1 = Object.keys(tr).length;
      console.log({ n1 });
      if (n1 > 0) {
        this.PageTitle = tr.PageTitle;
        this.PageDescription = tr.PageDescription;

        var CurrentJciYearId = parseInt(tr.CurrentJciYearId);
        console.log({ CurrentJciYearId });

        var YearName = parseInt(tr.CurrentJciYearName);
        console.log({ YearName });
        this.YearName = YearName;

        this.CurrentYearId = CurrentJciYearId;

        this.pageData();
      }
    },
    CategoryType: function () {
      console.log("watch CategoryType");
      var CategoryType = this.CategoryType;
      console.log({CategoryType});
      if (CategoryType == 3) {
        this.getDesignationCodeOptions();
      }
    },
    PortfolioTypeOptions: function () {
      console.log("watch PortfolioTypeOptions");
      this.PortfolioTypeOptionsLoading = false;
    },
    RecognitionGiftOptions: function () {
      console.log("watch RecognitionGiftOptions");
      this.RecognitionGiftOptionsLoading = false;
    },
    DesignationCodeOptions: function () {
      console.log("watch DesignationCodeOptions");
      this.DesignationCodeOptionsLoading = false;
    },
  },
  mounted() {},
  methods: {
    refreshPageData() {
      console.log("refreshPageData called");
      this.LoadingFlag = true;
      var url1 = "api/menu-details/show";
      var condition1 = {
        UserInterface: 1,
        Module: "np_recognition_category",
        Action: "create",
      };
      this.getPageDetails("PageInfo", {}, url1, condition1);
      this.LoadingFlag = false;
    },
    pageData() {
      console.log("pageData called");
      this.getPortfolioTypeOptions();
      this.getRecognitionGiftOptions();
    },
    closePrompt() {
      console.log("closePrompt called");
      this.$emit("hideAddRecordPrompt", this.ResultFlag);
    },
    getRecognitionGiftOptions() {
      console.log("getRecognitionGiftOptions called");
      this.RecognitionGiftOptionsLoading = true;
      const selectbox1_source = "RecognitionGift";
      const selectbox1_destination = "RecognitionGiftOptions"; // must match autocomplete items
      const selectbox1_url = "api/recognition-gift/options";
      const selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    getPortfolioTypeOptions() {
      console.log("getPortfolioTypeOptions called");
      var selectbox1_source = "PortfolioType";
      var selectbox1_destination = "PortfolioTypeOptions";
      var selectbox1_url = "api/portfolio/options";
      var selectbox1_conditions_array = {
        UserInterface: 1,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },

    getDesignationCodeOptions() {
      console.log("getDesignationCodeOptions called");
      this.DesignationAreaCode = "";
      this.DesignationZoneCode = "";
      this.DesignationLomCode = "";
      this.DesignationCodeOptionsLoading = true;
      var selectbox1_source = "DesignationCode";
      var selectbox1_destination = "DesignationCodeOptions";
      var selectbox1_url = "api/designation/options";
      var selectbox1_conditions_array = {
        UserInterface: 3,
        OrganizationTypeId: this.OrganizationType,
        AllOption: 0,
      };
      this.getSelectBoxOptions(
        selectbox1_source,
        selectbox1_destination,
        selectbox1_conditions_array,
        selectbox1_url
      );
    },
    confirmAlert() {
      var validate1 = this.$refs.form1.validate();
      console.log("validate1=" + validate1);
      if (validate1) {
        Swal.fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: `Save`,
          confirmButtonColor: "#3085d6",
          cancelButtonText: `Cancel`,
          cancelButtonColor: "#d33",
        }).then((result) => {
          /* Read more about isConfirmed, isDenied below */
          if (result.isConfirmed) {
            this.submitForm();
          } else {
            this.sweetAlert("error", "You cancelled the process", true);
          }
        });
      } else {
        // this.toast("error", "Kindly fill the required fields", true);
      }
    },
    submitForm() {
      console.log("submitForm is called");

      this.SubmitFlag = true;
      this.OverlayLoadingFlag = true;
      this.ResultFlag = false;

      var upload = {
        UserInterface: 1,
        CategoryTitle: this.CategoryTitle,
        CategoryDescription: this.CategoryDescription,
        CategoryMaxCount: this.CategoryMaxCount,
        CategoryType: this.CategoryType,
        CategoryDesignation: this.DesignationCode,
        Portfolio: this.PortfolioType,
        RecognitionGift: this.RecognitionGift,
      };
      console.log("upload=" + JSON.stringify(upload));

      var server_url = companyConfig.apiURL;
      var token = this.$session.get("token");
      token = token == (null || undefined) ? 0 : token;

      var add_url = server_url + "api/recognition-category/store";
      console.log(
        "server_url=" + server_url + ", token=" + token + ", add_url=" + add_url
      );

      const thisIns = this;
      var output = "";
      var records = "";
      var flag = 0;

      this.$http({
        url: add_url,
        method: "POST",
        data: upload,
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + token,
        },
        onUploadProgress: function (progressEvent) {
          this.uploadPercentage = parseInt(
            Math.round((progressEvent.loaded / progressEvent.total) * 100)
          );
        }.bind(this),
      })
        .then(function (response) {
          console.log("response=" + JSON.stringify(response));

          thisIns.SubmitFlag = false;
          thisIns.OverlayLoadingFlag = false;

          output = response.data.output;
          flag = response.data.flag;
          console.log("output=" + output + ", flag=" + flag);

          records = response.data.records;
          console.log({ records });

          if (flag == 1) {
            thisIns.sweetAlert("success", output, true);
            thisIns.ResultFlag = true;
            thisIns.closePrompt();
          } else {
            thisIns.sweetAlert("error", output, false);
          }
        })
        .catch(function (error) {
          console.log("error=" + error);
          thisIns.SubmitFlag = false;
          thisIns.OverlayLoadingFlag = false;
        });
    },
  },

  beforeMount() {
    this.refreshPageData();
  },
};
</script>

<style lang="scss" scoped>
.page-header-gradient {
  background: linear-gradient(90deg, #004e92, #0a79df);
  border-radius: 10px 10px 0 0;
}

.v-card {
  max-height: 85vh;
  overflow-y: auto;
}

/* Custom scrollbar styling */
.v-card::-webkit-scrollbar {
  width: 8px;
}

.v-card::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.v-card::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.v-card::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
